import React, { FC, useState, useEffect } from 'react'
import { throttle, debounce } from 'lodash'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useApolloClient } from '@apollo/client'
import useDraft from '../../redux/draft-drawer/useDraft'
import { PageLayout } from '../System'
import { SkeletonLoader } from '../SkeletonLoader'
import { EmptyState } from '../EmptyState'
import { QuickDraftOverwriteModal } from '../Modals'
import { Sortable } from '../System/Sortable'
import { theme } from '../utils/theme'
import { StyledDraftsView } from '.'
import useDraftsList from '../../redux/drafts-list/useDraftsList'
import useSearchParams from '../utils/useSearchParams'
import useSegment from '../utils/useSegment'
import { DraftItem } from './DraftItem'
import {
  SearchUserDrafts_searchUserDrafts_docs,
  SearchUserDrafts
} from '../../graphql/UserDraft/__generated__/SearchUserDrafts'
import { searchUserDraftsQuery } from '../../graphql/UserDraft/searchUserDrafts'
import DraftsHeader from './DraftsHeader'

const DraftsView: FC = () => {
  const {
    drafts,
    loading,
    page,
    categories,
    onlyLiked,
    search,
    sort,
    hideUsed,
    view,
    actions: {
      setDrafts, loadParams, setView, handleDrag, changePage
    }
  } = useDraftsList()
  const {
    _id,
    isSaved,
    actions: { setDraft, openDraft }
  } = useDraft()
  const { track } = useSegment()
  const apollo = useApolloClient()

  const [showSwitchModal, setShowSwitchModal] = useState(false)
  const [switchDraftId, setSwitchDraftId] = useState('')
  const [dragDisabled, setDragDisabled] = useState(false)
  const [totalDocs, setTotalDocs] = useState(0)

  const isMobile = window.innerWidth < theme.breakpoints.m
  const isEmptySearch = drafts.length === 0 && categories.length > 0

  const { getSearchFromURL } = useSearchParams({
    search,
    categories,
    sort,
    onlyLiked,
    hideUsed
  })

  // Sends the search event to Segment
  const handleSearchTrack = debounce(() => {
    track('Searched Draft Library', {
      query: search,
      sort,
      filters: categories,
      only_liked: onlyLiked,
      hide_used: hideUsed
    })
  }, 3000)

  const handleRows = (drafts: SearchUserDrafts_searchUserDrafts_docs[]) => {
    const rowsArray = drafts.map((draft) => (
      <DraftItem
        draft={draft}
        isCollapsed={view === 'grid'}
        isActive={_id === draft._id}
        key={draft._id}
        onClick={() => handleSwitch(draft._id)}
      />
    ))

    return rowsArray
  }

  const handleSwitch = (draftId: string) => {
    if (isSaved) {
      setDraft(draftId)
      openDraft()
      return
    }
    setSwitchDraftId(draftId)
    setShowSwitchModal(true)
  }

  const overwriteDraft = () => {
    setDraft(switchDraftId)
    openDraft()
  }

  useEffect(() => {
    let isSubscribed = true

    const loadDrafts = async () => {
      const { data } = await apollo.query<SearchUserDrafts>({
        query: searchUserDraftsQuery,
        fetchPolicy: 'no-cache',
        variables: {
          page,
          search,
          categories,
          liked: onlyLiked,
          hideUsed,
          sort,
          items: 10
        }
      })

      // prevent displaying of stale results from resolved query in previous render
      if (!isSubscribed) {
        return
      }

      const draftList = data.searchUserDrafts.docs || []
      setDrafts([...drafts, ...draftList])
      setTotalDocs(data?.searchUserDrafts?.totalDocs || 0)
    }

    if (loading && isSubscribed) {
      loadDrafts()
    }

    return () => {
      isSubscribed = false
    }
  }, [loading])

  useEffect(() => {
    loadParams(getSearchFromURL())
  }, [])

  useEffect(() => {
    // Disable custom sorting when there is a filter that is applied
    setDragDisabled(
      categories.length > 0 || onlyLiked || !!search.trim() || hideUsed || sort !== 'custom'
    )
  }, [categories, search, onlyLiked, hideUsed, sort])

  useEffect(() => {
    handleSearchTrack()

    return () => {
      handleSearchTrack.cancel()
    }
  }, [categories, search, onlyLiked, hideUsed])

  // Change grid view back to list if the screen size
  // is larger than the mobile breakpoint to re-allow
  // scrolling on desktop
  useEffect(() => {
    const handleResize = throttle(() => {
      if (window.innerWidth > theme.breakpoints.m) {
        setView('list')
      }
    }, 333)

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <>
      <PageLayout pageTitle="My Posts">
        <StyledDraftsView view={view}>
          <DraftsHeader />
          <div className="draft-list">
            {!loading && isEmptySearch && (
              <EmptyState
                title="No drafts were found"
                suggestion="Try another search term or filter"
              />
            )}
            {drafts.length > 0 && (
              <InfiniteScroll
                scrollThreshold={0.7}
                dataLength={drafts.length}
                next={() => changePage(page + 1)}
                hasMore={drafts.length < totalDocs}
                loader={null}
                className="infinite-scroll-component"
              >
                <Sortable
                  rows={handleRows(drafts)}
                  lockOffset={0}
                  axis="xy"
                  isDisabled={(view === 'list' && isMobile) || dragDisabled}
                  distance={2}
                  className="draft-grid-container"
                  helperClass="dragging"
                  onSortEnd={({ oldIndex, newIndex }) => handleDrag(oldIndex, newIndex)}
                />
              </InfiniteScroll>
            )}
            {loading && !drafts.length && (
              <div className="draft-grid">
                <SkeletonLoader type="square-list-item" quantity={6} />
              </div>
            )}
          </div>
        </StyledDraftsView>

        <QuickDraftOverwriteModal
          isOpen={showSwitchModal}
          onClose={() => setShowSwitchModal(false)}
          onConfirm={() => overwriteDraft()}
        />
      </PageLayout>
    </>
  )
}

export default DraftsView
